import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import {
  useTable,
  useGlobalFilter,
  TableOptions,
  useSortBy,
  useRowSelect,
  Hooks,
  Cell,
  usePagination,
  useFilters,
} from 'react-table'
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableSortLabel,
  Checkbox,
  TableContainer,
  TablePagination,
  Tooltip,
} from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { colors } from '../const/colors'
import { TableSearch } from './Table/TableSearch'
import TableCSVLink from '../components/Table/TableCSVLink'

const TABLE_SX = {
  '& .MuiTableContainer-root': {
    background: colors.surface,
    borderRadius: colors.radiusTable,
    border: `1px solid ${colors.border}`,
    overflowX: 'auto',
    overflowY: 'hidden',
    padding: '0 8px 8px',
  },
  '& .MuiTable-root': {
    borderCollapse: 'separate',
    borderSpacing: '0 3px',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontSize: '12px',
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
    color: colors.slate,
    fontWeight: 700,
    border: 'none',
    py: '12px',
    whiteSpace: 'nowrap',
  },
  '& .MuiTableSortLabel-root': {
    color: `${colors.slate} !important`,
    fontWeight: '700 !important',
    fontSize: '12px',
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
  },
  '& .MuiTableSortLabel-root.Mui-active': {
    color: `${colors.navy} !important`,
  },
  '& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon': {
    color: `${colors.navy} !important`,
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    fontSize: '14px',
    color: colors.textBody,
    border: 'none',
    py: 0,
    height: '60px',
    transition: 'background-color .12s',
  },
  '& .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root:first-of-type': {
    borderTopLeftRadius: colors.radiusRow,
    borderBottomLeftRadius: colors.radiusRow,
  },
  '& .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root:last-of-type': {
    borderTopRightRadius: colors.radiusRow,
    borderBottomRightRadius: colors.radiusRow,
  },
  '& .MuiTableBody-root .MuiTableRow-hover:hover .MuiTableCell-root': {
    backgroundColor: colors.rowHover,
  },
  '& .MuiTableBody-root .MuiTableRow-root.Mui-selected .MuiTableCell-root': {
    backgroundColor: colors.rowSelected,
  },
  '& .MuiTableBody-root .MuiTableRow-root.Mui-selected:hover .MuiTableCell-root': {
    backgroundColor: colors.rowSelected,
  },
  '& .MuiCheckbox-root': {
    color: colors.borderCheck,
  },
  '& .MuiCheckbox-root.Mui-checked': {
    color: colors.navy,
  },
  '& .MuiCheckbox-root.Mui-disabled': {
    color: colors.slateLight,
  },
  '& .MuiTablePagination-root': {
    color: colors.textMuted,
    fontSize: '13px',
    mt: '2px',
  },
  '& .MuiTablePagination-select, & .MuiTablePagination-displayedRows': {
    fontSize: '13px',
  },
} as const

interface IRTTableProps<T extends object> extends TableOptions<T> {
  rowSelect?: boolean
  onSelectionChange?: (data: T[]) => void
  pagination?: boolean
  onRowClick?: (rowData: T) => void
  actionsComponent?: (rowData: T) => React.ReactNode
  getCellProps?: (cell: Cell<T, any>) => object
  getRowProps?: (rowData: T) => object
  gFilter?: boolean
  isRowSelectable?: (rowData: T) => boolean
  selectDisabledHint?: string
  csvExport?: boolean
  orderByProps?: string
  sortDesc?: boolean
  csvHeaders?: any
  filterDownloadCsvValue?: string
  filterDownloadCsvKeys?: string
  csvFileName?: string
  children?: React.ReactNode
  toolbarSlot?: React.ReactNode
  serverSide?: IRTTableServerSide
}

export interface IRTTableServerSide {
  total: number
  page: number
  pageSize: number
  rowsPerPageOptions?: number[]
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  sortBy?: { id: string; desc: boolean }
  onSortChange?: (id: string, desc: boolean) => void
  labelDisplayedRows?: (info: {
    from: number
    to: number
    count: number
  }) => string
}

const defaultPropGetter = () => ({})

const RTTable = <T extends object>({
  columns,
  data,
  rowSelect,
  onSelectionChange,
  pagination,
  onRowClick,
  actionsComponent,
  isRowSelectable,
  selectDisabledHint,
  getCellProps = defaultPropGetter,
  getRowProps = defaultPropGetter,
  gFilter = true,
  hiddenColumns = [],
  csvExport,
  orderByProps = '',
  sortDesc = true,
  csvHeaders,
  filterDownloadCsvValue,
  filterDownloadCsvKeys,
  csvFileName = 'table-data-export.csv',
  children,
  toolbarSlot,
  serverSide,
}: IRTTableProps<T>) => {
  const selectionColumnId = 'selection'
  const actionColumnId = 'action'
  const getCellPadding = (columnId: string) =>
    columnId === selectionColumnId
      ? 'checkbox'
      : columnId === actionColumnId
      ? 'none'
      : undefined

  const isRowSelectableRef = useRef(isRowSelectable)
  isRowSelectableRef.current = isRowSelectable

  const onSelectionChangeRef = useRef(onSelectionChange)
  onSelectionChangeRef.current = onSelectionChange

  const hiddenColumnsKey = hiddenColumns.join(',')
  const columnsToHideKey = columns
    .filter(column => column.isVisible === false)
    .map(column => column.accessor as string)
    .join(',')

  const canSelect = useCallback((rowData: T) => {
    const predicate = isRowSelectableRef.current
    return !predicate || predicate(rowData)
  }, [])

  const blockedCheckbox = (
    <Checkbox
      checked={false}
      disabled
      icon={<LockOutlinedIcon sx={{ fontSize: 19 }} />}
    />
  )

  const withSelectHint = (checkbox: React.ReactElement, disabled: boolean) => {
    const wrapper = (
      <Box
        component="span"
        aria-label={disabled ? selectDisabledHint : undefined}
        onClick={e => e.stopPropagation()}
        sx={{ display: 'inline-flex' }}
      >
        {checkbox}
      </Box>
    )

    return disabled && selectDisabledHint ? (
      <Tooltip title={selectDisabledHint} placement="top" arrow>
        {wrapper}
      </Tooltip>
    ) : (
      wrapper
    )
  }

  const customHooks = (hooks: Hooks<T>) => {
    rowSelect &&
      hooks.visibleColumns.push(columns => [
        {
          id: selectionColumnId,
          Header: ({ rows, toggleRowSelected }: any) => {
            const selectable = rows.filter((row: any) =>
              canSelect(row.original),
            )
            const selectedCount = selectable.filter(
              (row: any) => row.isSelected,
            ).length
            const allSelected =
              selectable.length > 0 && selectedCount === selectable.length
            const disabled = selectable.length === 0
            return withSelectHint(
              disabled ? (
                blockedCheckbox
              ) : (
                <Checkbox
                  checked={allSelected}
                  indeterminate={selectedCount > 0 && !allSelected}
                  onChange={() =>
                    selectable.forEach((row: any) =>
                      toggleRowSelected(row.id, !allSelected),
                    )
                  }
                />
              ),
              disabled,
            )
          },
          Cell: ({ row }: any) => {
            const selectable = canSelect(row.original)
            return withSelectHint(
              selectable ? (
                <Checkbox
                  {...row.getToggleRowSelectedProps()}
                  checked={row.isSelected}
                />
              ) : (
                blockedCheckbox
              ),
              !selectable,
            )
          },
        },
        ...columns,
      ])
    actionsComponent &&
      hooks.visibleColumns.push((columns: any) => [
        ...columns,
        {
          id: actionColumnId,
          Cell: ({ row }: any) => actionsComponent(row.original),
        },
      ])
  }

  const initialState = useMemo(
    () => ({
      hiddenColumns,
      pageSize: 20,
      sortBy: [{ id: orderByProps, desc: sortDesc }],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hiddenColumnsKey, orderByProps, sortDesc],
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    gotoPage,
    setPageSize,
    selectedFlatRows,
    state: { pageIndex, pageSize, globalFilter },
    setGlobalFilter,
    setHiddenColumns,
  } = useTable(
    {
      columns,
      data,
      initialState,
      autoResetHiddenColumns: false,
      manualSortBy: !!serverSide,
      manualPagination: !!serverSide,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect,
    customHooks,
  )

  useEffect(() => {
    setHiddenColumns(columnsToHideKey ? columnsToHideKey.split(',') : [])
  }, [columnsToHideKey, setHiddenColumns])

  const selectedRowsRef = useRef(selectedFlatRows)
  selectedRowsRef.current = selectedFlatRows
  const selectedIdsKey = selectedFlatRows.map(row => row.id).join(',')

  useEffect(() => {
    const notifySelection = onSelectionChangeRef.current
    if (!notifySelection) return
    notifySelection(
      selectedRowsRef.current
        .map(row => row.original)
        .filter(row => canSelect(row)),
    )
  }, [selectedIdsKey, canSelect])

  return (
    <Box sx={TABLE_SX}>
      <Box
        sx={{
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          mb: '18px',
          flexWrap: 'wrap',
        }}
      >
        {gFilter && (
          <TableSearch
            value={globalFilter ?? ''}
            onChange={val => setGlobalFilter(val || undefined)}
          />
        )}
        {toolbarSlot}
        {csvExport && (
          <TableCSVLink
            csvHeaders={csvHeaders}
            fileName={csvFileName}
            data={selectedFlatRows}
            filterDownloadCsvValue={filterDownloadCsvValue}
            filterDownloadCsvKeys={filterDownloadCsvKeys}
          />
        )}
      </Box>
      {children}
      <TableContainer>
        <TableStyled {...getTableProps()}>
          <TableHead>
            {headerGroups.map((headerGroup, groupIndex) => (
              <TableRow
                {...headerGroup.getHeaderGroupProps()}
                key={headerGroup.headers.map(column => column.id).join('|') ||
                  groupIndex}
              >
                {headerGroup.headers.map(column => (
                  <TableCell
                    {...column.getHeaderProps()}
                    padding={getCellPadding(column.id)}
                    key={column.id}
                  >
                    <TableSortLabel
                      data-e2e={`sort-${column.id}`}
                      active={
                        serverSide
                          ? serverSide.sortBy?.id === column.id
                          : column.isSorted
                      }
                      direction={
                        (
                          serverSide
                            ? serverSide.sortBy?.id === column.id &&
                              serverSide.sortBy.desc
                            : column.isSortedDesc
                        )
                          ? 'desc'
                          : 'asc'
                      }
                      onClick={() => {
                        if (serverSide) {
                          if (!column.canSort || !serverSide.onSortChange)
                            return
                          const active = serverSide.sortBy?.id === column.id
                          serverSide.onSortChange(
                            column.id,
                            !(active && serverSide.sortBy?.desc),
                          )
                          return
                        }
                        column.id !== 'selection' &&
                          (column.isSortedDesc
                            ? column.toggleSortBy(false, false)
                            : column.toggleSortBy(true, false))
                      }}
                    >
                      <>{column.render('Header')}</>
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody {...getTableBodyProps()}>
            {(pagination ? page : rows).map(row => {
              prepareRow(row)
              return (
                <TableRowStyled
                  {...row.getRowProps([getRowProps(row.original)])}
                  key={row.id}
                  hover
                  selected={row.isSelected}
                  isClickable={!!onRowClick}
                  onClick={() => onRowClick && onRowClick(row.original)}
                >
                  {row.cells.map(cell => (
                    <TableCell
                      {...cell.getCellProps([getCellProps(cell)])}
                      padding={getCellPadding(cell.column.id)}
                      key={cell.column.id}
                    >
                      <>{cell.render('Cell')}</>
                    </TableCell>
                  ))}
                </TableRowStyled>
              )
            })}
          </TableBody>
        </TableStyled>
      </TableContainer>

      {pagination && serverSide && (
        <TablePagination
          component="div"
          count={serverSide.total}
          page={serverSide.page}
          onPageChange={(_e, page) => serverSide.onPageChange(page)}
          rowsPerPageOptions={serverSide.rowsPerPageOptions ?? [10, 20, 30]}
          rowsPerPage={serverSide.pageSize}
          onRowsPerPageChange={e =>
            serverSide.onPageSizeChange(Number(e.target.value))
          }
          labelDisplayedRows={serverSide.labelDisplayedRows}
        />
      )}
      {pagination && !serverSide && (
        <TablePagination
          component="div"
          count={rows.length}
          page={pageIndex}
          onPageChange={(_e, page) => gotoPage(page)}
          rowsPerPageOptions={[10, 20, 30]}
          rowsPerPage={pageSize}
          onRowsPerPageChange={e => setPageSize(Number(e.target.value))}
        />
      )}
    </Box>
  )
}

export default RTTable

const TableRowStyled = styled(
  ({
    isClickable: _ic,
    ...props
  }: { isClickable?: boolean } & React.ComponentProps<typeof TableRow>) => (
    <TableRow {...props} />
  ),
)<{ isClickable?: boolean }>`
  ${({ isClickable }) => isClickable && 'cursor: pointer;'}
`
const TableStyled = styled(Table)`
  overflow-x: auto;
`
